import { buildMapId } from "@dailywars/core/engine";
import { CALENDAR_START_DATE } from "@dailywars/core/entitlements/mapAccess";

type CalendarDayVariant = { mapId: string; bestDays: number | null };

export type CalendarMonthDay = {
  dayNumber: number;
  calendarDate: string;
  standard: CalendarDayVariant;
  premium: CalendarDayVariant;
};

export type CalendarMonthBucket = {
  monthKey: string;
  monthLabel: string;
  days: CalendarMonthDay[];
};

const monthLabelFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

export function formatCalendarMonthLabel(monthKey: string): string {
  const year = Number(monthKey.slice(0, 4));
  const monthIndex = Number(monthKey.slice(5, 7)) - 1;
  return monthLabelFormatter.format(new Date(Date.UTC(year, monthIndex, 1)));
}

function datesInMonthClipped({
  monthKey,
  todayStr,
}: {
  monthKey: string;
  todayStr: string;
}): string[] {
  const year = Number(monthKey.slice(0, 4));
  const month = Number(monthKey.slice(5, 7));
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const dates: string[] = [];

  for (let day = lastDay; day >= 1; day -= 1) {
    const dateStr = `${monthKey}-${String(day).padStart(2, "0")}`;
    if (dateStr > todayStr || dateStr < CALENDAR_START_DATE) {
      continue;
    }
    dates.push(dateStr);
  }

  return dates;
}

function calendarMonthDayFromDate(dateStr: string): CalendarMonthDay {
  return {
    dayNumber: Number(dateStr.slice(8, 10)),
    calendarDate: dateStr,
    standard: {
      mapId: buildMapId(dateStr, "standard"),
      bestDays: null,
    },
    premium: {
      mapId: buildMapId(dateStr, "premium"),
      bestDays: null,
    },
  };
}

export function buildCalendarMonthBucket({
  monthKey,
  todayStr,
}: {
  monthKey: string;
  todayStr: string;
}): CalendarMonthBucket {
  const days = datesInMonthClipped({ monthKey, todayStr }).map(calendarMonthDayFromDate);

  return {
    monthKey,
    monthLabel: formatCalendarMonthLabel(monthKey),
    days,
  };
}
